// Per-årsmodell FAKTA för /husbil-arsmodell-[år].
//
// Viktigt: tidigare byggdes dessa sidor på intervall (year >= 2020 ...), vilket
// gav flera årtal exakt samma text = dubblettinnehåll. Nu har varje årsmodell
// egna, årsspecifika fakta: vad som faktiskt hände i modellåret, typiskt
// mätarställning, uppskattat prisspann, värdeminskning och de fel vi själva ser
// på verkstaden. Alla siffror är våra uppskattningar för den svenska
// andrahandsmarknaden och ska läsas som spann, inte exakta värden.

export interface ArsmodellFakta {
  year: number;
  ageYears: number;
  headline: string;
  /** Vad som var nytt/karaktäristiskt just detta modellår. */
  nytt: string;
  tech: string;
  /** Marknadsläget för årsmodellen idag. */
  marknad: string;
  priceRange: string;
  /** Typisk mätarställning i mil för en välskött bil av årsmodellen. */
  typiskMil: string;
  /** Uppskattad total värdeminskning från nypris. */
  vardeminskning: string;
  /** Det vi oftast hittar vid fukt- och gasoltest på årsmodellen. */
  vanligaFel: string[];
  garanti: string;
  perfectFor: string;
}

export const ARSMODELL_FAKTA: ArsmodellFakta[] = [
  {
    year: 2018,
    ageYears: 8,
    headline: "2018 — sista stora året för absorptionskyl, Euro 6b-chassin",
    nytt: "Fiat Ducato med 2,3 MultiJet II i 130/150 hk dominerade, fortfarande Euro 6b. Många tillverkare erbjöd LED-belysning genomgående för första gången som standard.",
    tech: "Absorptionskyl (3-vägs) vanligast, Truma Combi 4 på gas, 95 Ah AGM, 100 W solpanel som tillval.",
    marknad: "Stort utbud i Sverige — 2018 var ett rekordår för husbilsregistreringar, vilket gör att du kan förhandla. Bilar som stått ute alla vintrar bör fuktmätas extra noga.",
    priceRange: "Uppskattat 450 000 – 1 300 000 kr beroende på segment",
    typiskMil: "9 000 – 16 000 mil",
    vardeminskning: "Ca 40–50 % från nypris",
    vanligaFel: [
      "Fukt i bakvägg/hörn där tätning aldrig efterdragits",
      "Absorptionskylen tappar effekt vid över 30 °C utetemperatur",
      "AGM-batteriet är ofta uttjänt — räkna med byte",
    ],
    garanti: "Fabriksgarantin är slut. Rapidos 6-åriga karossgaranti gick ut 2024 — kräv dokumenterade årliga tätningskontroller.",
    perfectFor: "Dig som vill ha modern nog utrustning till klart lägre pris än 2020+, och som accepterar att batteri och kyl kan behöva åtgärdas.",
  },
  {
    year: 2019,
    ageYears: 7,
    headline: "2019 — kompressorkylen slår igenom, Euro 6d-TEMP kommer",
    nytt: "Övergången till Euro 6d-TEMP under året gör att två bilar av samma modell 2019 kan ha olika motorgeneration — kolla registreringsdatum, inte bara modellår.",
    tech: "Kompressorkyl på fler modeller, Truma Combi 4/6, 95 Ah AGM standard, 100–160 W sol allt vanligare.",
    marknad: "Prisvärt mellanläge: nyare teknik än 2018 men utan 2020-årens prisrusning. Den årsmodell vi oftast rekommenderar till förstagångsköpare med 600 000 kr.",
    priceRange: "Uppskattat 500 000 – 1 500 000 kr beroende på segment",
    typiskMil: "8 000 – 14 000 mil",
    vardeminskning: "Ca 35–45 % från nypris",
    vanligaFel: [
      "Takluckor med sprickor i plasten efter sju norrlandsvintrar",
      "Gasolslangar utgångna (10-årsgräns närmar sig)",
      "Fukt runt markiser som monterats i efterhand",
    ],
    garanti: "Fabriksgaranti slut, karossgaranti löper ut 2025–2026 beroende på leveransdatum.",
    perfectFor: "Köpare som vill ha kompressorkyl och modern motor utan att betala pandemiårens priser.",
  },
  {
    year: 2020,
    ageYears: 6,
    headline: "2020 — solpanel blir standard, produktionsstopp i våras",
    nytt: "Fabrikerna i Frankrike och Tyskland stod stilla under våren, så 2020-bilar är ovanligt få. De som finns är ofta högt utrustade eftersom beställningsbilar prioriterades.",
    tech: "100–200 W solpanel som standard på fler serier, kompressorkyl 90–175 l, LED genomgående, litium som tillval.",
    marknad: "Litet utbud och därför envisa priser. Här lönar det sig att jämföra med 2021 innan du slår till.",
    priceRange: "Uppskattat 600 000 – 1 800 000 kr beroende på segment",
    typiskMil: "6 000 – 12 000 mil",
    vardeminskning: "Ca 30–40 % från nypris",
    vanligaFel: [
      "Solregulator av äldre PWM-typ som begränsar laddningen",
      "Fukt vid solpanelens genomföring när den monterats av handlare",
      "Lite körda bilar: torra bromsskivor och gamla däck trots låg mil",
    ],
    garanti: "Karossgaranti kan finnas kvar till 2026 — begär garantibevis och servicehistorik.",
    perfectFor: "Dig som vill ha nästan-ny utrustning men slippa den första och största värdeminskningsdippen.",
  },
  {
    year: 2021,
    ageYears: 5,
    headline: "2021 — husbilsboomen, allt såldes innan det byggdes",
    nytt: "Rekordefterfrågan gjorde att många bilar levererades med reducerad utrustning (komponentbrist) — två likadana 2021-bilar kan skilja sig i värmesystem och kyl.",
    tech: "Kompressorkyl standard i de flesta serier, Truma Combi 6, litium på tillvalslistan, MPPT-regulator på premiumserier.",
    marknad: "Stort utbud eftersom många boom-köpare säljer nu efter fyra–fem säsonger. Bra läge för köpare, tufft för säljare.",
    priceRange: "Uppskattat 650 000 – 2 000 000 kr beroende på segment",
    typiskMil: "4 000 – 10 000 mil",
    vardeminskning: "Ca 25–35 % från nypris",
    vanligaFel: [
      "Utrustningslistan stämmer inte med annonsen — verifiera mot originalorder",
      "Lågt körda bilar med stillaståendeskador i drivlinan",
      "Fukt bakom eftermonterad cykelhållare",
    ],
    garanti: "Karossgaranti oftast kvar till 2027 om årliga kontroller är gjorda.",
    perfectFor: "Familjer som vill ha maximal utrustning per krona och kan lägga tid på att kontrollera specifikation.",
  },
  {
    year: 2022,
    ageYears: 4,
    headline: "2022 — prishöjningar och långa leveranstider, litium blir vanligt",
    nytt: "Prislistorna höjdes flera gånger under året, vilket gör att nypriset varierar kraftigt inom samma modellår. Litiumbatteri gick från exotiskt till vanligt tillval.",
    tech: "Litium 100–200 Ah som populärt tillval, MPPT-sol, Truma Combi 6 D (diesel) på fler modeller, dubbelglasade fönster på premiumserier.",
    marknad: "Fortfarande relativt höga priser eftersom nypriset var högt. Jämför alltid mot en 2023 innan du bestämmer dig — skillnaden är ofta mindre än väntat.",
    priceRange: "Uppskattat 750 000 – 2 300 000 kr beroende på segment",
    typiskMil: "3 000 – 8 000 mil",
    vardeminskning: "Ca 20–30 % från nypris",
    vanligaFel: [
      "Litiumsystem installerade utan låg-temperaturskydd — laddning under 0 °C skadar cellerna",
      "Programvara i Truma iNet som aldrig uppdaterats",
      "Ojämn kvalitet i inredningsdetaljer från komponentbristens år",
    ],
    garanti: "Karossgaranti normalt kvar till 2028. Fabriksgarantin på boendedelen är slut.",
    perfectFor: "Dig som vill ha litium och dieselvärme men inte betala nybilspris.",
  },
  {
    year: 2023,
    ageYears: 3,
    headline: "2023 — ny designgeneration och tillbaka till normala leveranstider",
    nytt: "Flera tillverkare (bland andra Rapido) rullade ut nya interiörprogram och Ducato med 9-stegs automat blev ett realistiskt val i stället för en väntelista.",
    tech: "Kompressorkyl 90–175 l, USB-C genomgående, Combi 6 D vanligt, litium i paket, kollisionsvarnare på fler chassin.",
    marknad: "Mest attraktiva årsmodellen just nu i vår butik: nytt skick, låg mil, men första värdeminskningen tagen av förste ägaren.",
    priceRange: "Uppskattat 850 000 – 2 600 000 kr beroende på segment",
    typiskMil: "2 000 – 6 000 mil",
    vardeminskning: "Ca 15–25 % från nypris",
    vanligaFel: [
      "Sällan tekniska fel — det vi hittar är kosmetiska transportskador",
      "Fuktvärden nästan alltid under 12 % om bilen stått inne",
      "Däck från 2022 trots låg mil — kontrollera DOT-märkning",
    ],
    garanti: "Karossgaranti kvar till 2029. Delar av leverantörsgarantier på vitvaror kan gälla.",
    perfectFor: "Kunder som vill ha nytt-skick utan att betala full nybilspremie.",
  },
  {
    year: 2024,
    ageYears: 2,
    headline: "2024 — litium närmar sig standard, mer förarassistans",
    nytt: "Adaptiv farthållare och filhållningsassistans blev tillgängligt på fler husbilschassin, och litium blev standard i flera premiumpaket i stället för tillval.",
    tech: "Litium standard i premiumpaket, MPPT, Truma iNet X, dubbelglas, 200 W sol på fler modeller.",
    marknad: "Begränsat begagnatutbud — de flesta 2024-bilar har fortfarande sin förste ägare. Prisbilden ligger nära ny bil, så jämför alltid mot en konfigurerad nybil.",
    priceRange: "Uppskattat 900 000 – 2 800 000 kr beroende på segment",
    typiskMil: "1 000 – 4 000 mil",
    vardeminskning: "Ca 10–18 % från nypris",
    vanligaFel: [
      "Nästan inga — men kontrollera att första serviceintervallet är gjort i tid",
      "Mjukvaruuppdateringar på chassit som inte körts",
    ],
    garanti: "Fabriksgaranti kan vara kvar (24 mån från leverans) plus karossgaranti till 2030.",
    perfectFor: "Dig som vill nästan-ny bil med garanti kvar, men slippa nybilsmomsdippen.",
  },
  {
    year: 2025,
    ageYears: 1,
    headline: "2025 — ettåringarna, garanti kvar och minimal värdeminskning tagen",
    nytt: "Årsmodell 2025 innebär i praktiken senaste generationens el- och värmesystem med litium och app-styrning som standard på de flesta halvintegrerade.",
    tech: "Litium standard eller nära, MPPT, Truma iNet-system, ABS/ESP + kollisionsvarning, Ducato 9-stegs automat.",
    marknad: "Fåtal på marknaden — oftast demobilar eller inbyten. Prisskillnaden mot ny 2026 är liten, så räkna på båda.",
    priceRange: "Uppskattat 950 000 – 3 000 000 kr beroende på modell",
    typiskMil: "200 – 2 000 mil",
    vardeminskning: "Ca 5–12 % från nypris",
    vanligaFel: [
      "Inga tekniska fel att vänta — kontrollera i stället att garantin är korrekt registrerad på dig",
    ],
    garanti: "Full fabriksgaranti kvar (24 mån) + 6 års karossgaranti på Rapido.",
    perfectFor: "Dig som vill ha nybilstrygghet men hellre tar en demobil med rabatt.",
  },
  {
    year: 2026,
    ageYears: 0,
    headline: "2026 — årets modeller, konfigurera precis som du vill",
    nytt: "Nya modellåret beställs efter dina val: planlösning, värmesystem, batterikapacitet och draglist. Leveranstiden styr när du kan hämta i Kalix.",
    tech: "Litium, MPPT, iNet, förarassistans och 9-stegs automat i de flesta konfigurationer. Dieselvärme går att välja bort gasol helt.",
    marknad: "Inget begagnatutbud — här handlar det om nybilskonfiguration och byte av din nuvarande bil. Vi räknar in bytesbil och visar mellanpris direkt.",
    priceRange: "Uppskattat 1 050 000 – 3 200 000 kr enligt aktuell prislista",
    typiskMil: "0 mil",
    vardeminskning: "0 % — men räkna med 10–15 % första året",
    vanligaFel: [
      "Inga — men välj värmesystem efter var du ska stå på vintern, det är det val kunder oftast ångrar",
    ],
    garanti: "24 månaders fabriksgaranti + 6 års karossgaranti (Rapido), förutsatt årlig tätningskontroll hos oss.",
    perfectFor: "Dig som vill ha exakt rätt planlösning och full garanti från dag ett.",
  },
];

export function getArsmodellFakta(year: number): ArsmodellFakta | undefined {
  return ARSMODELL_FAKTA.find((a) => a.year === year);
}

/** All unik text för en årsmodell — används av kvalitetsgrinden. */
export function arsmodellText(a: ArsmodellFakta): string {
  return [
    a.headline,
    a.nytt,
    a.tech,
    a.marknad,
    a.priceRange,
    a.typiskMil,
    a.vardeminskning,
    ...a.vanligaFel,
    a.garanti,
    a.perfectFor,
  ].join(" ");
}
